import { useQuery } from 'react-query';
import { PageHeader, FilterBar, EmptyState, StatusChip } from '../components/ui';
import {
  Box,
  TextField,
  MenuItem,
  Button
} from '@mui/material';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { adminAPI } from '../services/api';
import useListQueryParams from '../hooks/useListQueryParams';
import AdminDateRangeFilter from '../components/AdminDateRangeFilter.jsx';
import './ModuleAdmin.css';

function formatWhen(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return x.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata'
  });
}

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' }
];

const ActivityLogs = () => {
  const { query, setQuery } = useListQueryParams({
    page: '1',
    limit: '50',
    search: '',
    module: '',
    action: '',
    fromDate: '',
    toDate: '',
    sortOrder: 'desc'
  });

  const page = Number(query.page || 1);
  const limit = Number(query.limit || 50);
  const search = query.search || '';
  const moduleFilter = query.module || '';
  const actionFilter = query.action || '';
  const fromDate = query.fromDate || '';
  const toDate = query.toDate || '';
  const sortOrder = query.sortOrder || 'desc';

  const params = { page, limit, sortOrder };
  if (search.trim()) params.search = search.trim();
  if (moduleFilter) params.module = moduleFilter;
  if (actionFilter) params.action = actionFilter;
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-activity-logs', page, limit, search, moduleFilter, actionFilter, fromDate, toDate, sortOrder],
    queryFn: () => adminAPI.getActivityLogs(params)
  });

  const list = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: 0 };
  const moduleOptions = data?.data?.filters?.modules || [];

  return (
    <div className="module-page">
      <PageHeader
        title={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <HistoryRoundedIcon color="primary" />
            Activity logs
          </Box>
        }
        subtitle="Who changed what in the admin panel — user name, role, time, and action summary. New entries appear when staff create, update, or delete records (registrations, treks, content, users, roles, etc.)."
      />

      <FilterBar>
        <TextField
          size="small"
          label="Search"
          placeholder="Name, role, summary, path…"
          value={search}
          onChange={(e) => setQuery({ search: e.target.value, page: '1' })}
          sx={{ minWidth: 260 }}
        />
        <TextField
          size="small"
          select
          label="Module"
          value={moduleFilter}
          onChange={(e) => setQuery({ module: e.target.value, page: '1' })}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All modules</MenuItem>
          {moduleOptions.map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          label="Action"
          value={actionFilter}
          onChange={(e) => setQuery({ action: e.target.value, page: '1' })}
          sx={{ minWidth: 140 }}
        >
          {ACTION_OPTIONS.map((o) => (
            <MenuItem key={o.value || 'all'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <AdminDateRangeFilter
          startDate={fromDate}
          endDate={toDate}
          onStartDateChange={(v) => setQuery({ fromDate: v, page: '1' })}
          onEndDateChange={(v) => setQuery({ toDate: v, page: '1' })}
        />
        <TextField
          size="small"
          select
          label="Sort"
          value={sortOrder}
          onChange={(e) => setQuery({ sortOrder: e.target.value, page: '1' })}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="desc">Latest first</MenuItem>
          <MenuItem value="asc">Oldest first</MenuItem>
        </TextField>
      </FilterBar>

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading activity logs…</p>
        ) : list.length === 0 ? (
          <EmptyState
            title="No activity recorded yet"
            description="Actions will appear here when staff create, update, or delete records."
          />
        ) : (
          <table className="module-table">
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Role</th>
                <th>Action</th>
                <th>Module</th>
                <th>What happened</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatWhen(row.createdAt)}</td>
                  <td>
                    <strong>{row.userName || '—'}</strong>
                    {row.userEmail ? (
                      <span className="users-phone">{row.userEmail}</span>
                    ) : null}
                  </td>
                  <td>
                    <StatusChip status="default" label={row.userRole || '—'} />
                  </td>
                  <td>
                    <StatusChip status={row.action || 'default'} label={row.action || '—'} />
                  </td>
                  <td>{row.module || '—'}</td>
                  <td>
                    <div>{row.summary || '—'}</div>
                    {row.entityId ? (
                      <span className="users-phone" title={`Full ID: ${row.entityId}`}>
                        {/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(row.entityId)
                          ? `Ref: ${row.entityId.slice(0, 8)}…`
                          : `ID: ${row.entityId}`}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {pagination.totalPages > 1 && (
          <div className="module-list-pagination">
            <span>
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <div className="module-list-pagination-btns">
              <button
                type="button"
                className="module-btn secondary"
                disabled={page <= 1}
                onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}
              >
                Previous
              </button>
              <button
                type="button"
                className="module-btn secondary"
                disabled={page >= pagination.totalPages}
                onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
